"use client";

import type * as React from "react";
import { Switch } from "@/components/ui/switch";
import { cn } from "@/lib/utils";

export interface SwitchItemProps {
  id: string;
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
  label: React.ReactNode;
  description?: React.ReactNode;
  disabled?: boolean;
  invalid?: boolean;
  /** Grey the label while off — for lists, where "on" is the selection. */
  dimWhenOff?: boolean;
  className?: string;
}

/**
 * One toggle presented as an item. The whole item is the `<label>`, so a click
 * anywhere on it flips the switch exactly once. The outer `div` scopes the row:
 * `closest("div")` from its text reaches this row's switch and no other.
 */
export function SwitchItem({
  id,
  checked,
  onCheckedChange,
  label,
  description,
  disabled,
  invalid,
  dimWhenOff,
  className,
}: SwitchItemProps) {
  return (
    <div className={className}>
      <label
        htmlFor={id}
        className={cn(
          "flex select-none items-center justify-between gap-3 px-3 py-2 text-sm transition-colors",
          disabled ? "cursor-not-allowed opacity-60" : "cursor-pointer hover:bg-muted/50",
        )}
      >
        <span className="min-w-0 flex-1">
          <span className={cn("block", dimWhenOff && !checked && "text-muted-foreground")}>{label}</span>
          {description && <span className="mt-1 block text-xs text-muted-foreground">{description}</span>}
        </span>
        <Switch
          id={id}
          size="sm"
          checked={checked}
          disabled={disabled}
          aria-invalid={invalid}
          onCheckedChange={onCheckedChange}
        />
      </label>
    </div>
  );
}
